import { EntityIdAliasPrefixes } from '@abb-procure/constants';
import { InvitedUserModel, ShareApiService } from '@abb-procure/data';
import { SnackbarService } from '@abb-procure/snackbar';
import {
  Component,
  DestroyRef,
  Input,
  booleanAttribute,
  inject,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { DialogService } from '../../services/dialog.service';
import { Button } from '../button/button.component';
import { Tag } from '../tag/tag.component';

@Component({
  selector: 'procure-invited-user',
  templateUrl: './invited-user.component.html',
  styleUrl: './invited-user.component.scss',
  imports: [Button, MatIcon, Tag],
})
export class InvitedUser {
  @Input({ required: true }) invitedUser!: InvitedUserModel;
  @Input({ required: true }) entityType!: EntityIdAliasPrefixes;
  @Input({ required: true }) entityId?: string;
  @Input({ required: true, transform: booleanAttribute }) canRemove = false;

  readonly remove = output<void>();

  protected readonly isRemoving = signal(false);

  private readonly dialogService = inject(DialogService);
  private readonly shareApiService = inject(ShareApiService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);

  protected removeUser(): void {
    this.dialogService
      .showConfirmDialog({
        text: `Are you sure you want to remove ${this.invitedUser.userModel.name} from the list of invited users?`,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (!response) {
          return;
        }

        this.handleUserRemoval(this.invitedUser);
      });
  }

  private async handleUserRemoval(user: InvitedUserModel): Promise<void> {
    if (!user.userModel.id || !this.entityId) {
      return;
    }

    this.isRemoving.set(true);

    const response = await this.shareApiService.removeInvitedUser(
      this.entityType,
      this.entityId,
      user.userModel.id,
    );

    this.isRemoving.set(false);

    if (!response) {
      return;
    }

    this.snackbarService.showSuccessSnackbar({
      text: `Removed ${user.userModel.name} from the list of invited users.`,
    });

    this.remove.emit();
  }
}
